import { QueryKeys } from "@/constants/Query";
import { withMultipleEncountersHandler } from "@/features/Patient/MedicalRecords/MedicalHistory/useMedicalHistoryErrorHandler";
import type { ApiError } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import type { Condition, Encounter } from "fhir/r4";

export interface BillingCode {
  code: string;
  display: string;
}

export interface AddBillingCodeVariables {
  billingCode: BillingCode;
}

export interface AddBillingCodeResponse {
  condition: Condition;
  encounter: Encounter;
}

type NonEmptyArray<T> = [T, ...T[]];

export interface AddBillingCodesVariables {
  billingCodes: NonEmptyArray<BillingCode>;
}

export interface AddBillingCodesResponse {
  encounter: Encounter;
  billingCodes: Condition[];
}

export type RemoveBillingCodesVariables =
  | {
      conditionIds: NonEmptyArray<string>;
      icd10Codes?: string[];
    }
  | {
      conditionIds?: string[];
      icd10Codes: NonEmptyArray<string>;
    };

export interface RemoveBillingCodesResponse {
  encounter: Encounter;
}

export interface ReorderBillingCodeVariables {
  conditionId: string;
  position: number;
}

export interface ReorderBillingCodeResponse {
  encounter: Encounter;
}

interface BillingCodeMutationOptions<TResponse> {
  patientId: string;
  encounterId: string;
  onSuccess?: (response: TResponse) => void;
  onError?: (error: ApiError) => void;
}

export const useAddBillingCode = ({
  patientId,
  encounterId,
  onSuccess,
  onError,
}: BillingCodeMutationOptions<AddBillingCodeResponse>) => {
  const queryClient = useQueryClient();

  return useApiMutation<AddBillingCodeResponse, AddBillingCodeVariables>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.VisitClosure.ADD_BILLING_CODE({
        patientId,
        encounterId,
      }),
      mutationFn: async (variables) => {
        const response = await axiosInstance.post<AddBillingCodeResponse>(
          `/charting/encounter-charting/${encounterId}/billing-code/add`,
          variables,
        );

        return response.data;
      },
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.VisitDiagnoses({
            patientId,
            encounterId,
          }),
        });
        onSuccess?.(response);
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useAddBillingCodes = ({
  patientId,
  encounterId,
  onSuccess,
  onError,
}: BillingCodeMutationOptions<AddBillingCodesResponse>) => {
  const queryClient = useQueryClient();

  return useApiMutation<AddBillingCodesResponse, AddBillingCodesVariables>({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.VisitClosure.ADD_BILLING_CODES({
        patientId,
        encounterId,
      }),
      mutationFn: async (variables) => {
        const response = await axiosInstance.post<AddBillingCodesResponse>(
          `/charting/encounter-charting/${encounterId}/billing-codes/add`,
          variables,
        );

        return response.data;
      },
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.VisitDiagnoses({
            patientId,
            encounterId,
          }),
        });
        onSuccess?.(response);
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useRemoveBillingCodes = ({
  patientId,
  encounterId,
  onSuccess,
  onError,
}: BillingCodeMutationOptions<RemoveBillingCodesResponse>) => {
  const queryClient = useQueryClient();

  return useApiMutation<
    RemoveBillingCodesResponse,
    RemoveBillingCodesVariables
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.VisitClosure.REMOVE_BILLING_CODES({
        patientId,
        encounterId,
      }),
      mutationFn: async (variables) => {
        const response = await axiosInstance.post<RemoveBillingCodesResponse>(
          `/charting/encounter-charting/${encounterId}/billing-codes/remove`,
          variables,
        );

        return response.data;
      },
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.VisitDiagnoses({
            patientId,
            encounterId,
          }),
        });
        onSuccess?.(response);
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};

export const useReorderBillingCode = ({
  patientId,
  encounterId,
  onSuccess,
  onError,
}: BillingCodeMutationOptions<ReorderBillingCodeResponse>) => {
  const queryClient = useQueryClient();

  return useApiMutation<
    ReorderBillingCodeResponse,
    ReorderBillingCodeVariables
  >({
    mutationOptions: {
      mutationKey: QueryKeys.Patient.Chart.VisitClosure.REORDER_BILLING_CODE({
        patientId,
        encounterId,
      }),
      mutationFn: async ({ conditionId, position }) => {
        if (!Number.isInteger(position) || position < 1) {
          throw new AxiosError(
            "Billing code position must be a positive integer.",
          );
        }

        const response = await axiosInstance.post<ReorderBillingCodeResponse>(
          `/charting/encounter-charting/${encounterId}/billing-code/reorder`,
          { conditionId, position },
        );

        return response.data;
      },
      onSuccess: (response) => {
        queryClient.invalidateQueries({
          queryKey: QueryKeys.Patient.Chart.Conditions.VisitDiagnoses({
            patientId,
            encounterId,
          }),
        });
        onSuccess?.(response);
      },
      onError: withMultipleEncountersHandler(onError),
    },
  });
};
